import { Node } from '@tiptap/core';

export const VariableSelect = Node.create({
  name: 'variableSelect',
  group: 'inline',
  inline: true,
  atom: true,

  addAttributes() {
    return {
      variable: { default: null },
      value: { default: '' },
      label: { default: '' },
      placeholder: { default: '' },
      required: { default: false },
      options: { default: [] },
      multiple: { default: false },
    };
  },

  parseHTML() {
    return [
      {
        tag: 'span.variable-select',
        getAttrs: (dom) => ({
          variable: dom.dataset.variable || null,
          value: dom.dataset.value || '',
          placeholder: dom.dataset.placeholder || '',
          required: dom.dataset.required || false,
          options: dom.dataset.options ? dom.dataset.options.split(',') : [],
          multiple: dom.dataset.multiple === 'true',
        }),
      },
    ];
  },

  renderHTML({ HTMLAttributes }) {
    const attrs = {
      class: 'variable-select',
      'data-variable': HTMLAttributes.variable,
      'data-value': HTMLAttributes.value,
      'data-label': HTMLAttributes.label || HTMLAttributes.value || '',
      'data-placeholder': HTMLAttributes.placeholder,
    };

    if (HTMLAttributes.multiple) {
      attrs['data-multiple'] = 'true';
    }

    return ['span', attrs, HTMLAttributes.label || HTMLAttributes.value || ''];
  },

  addNodeView() {
    return ({ node, editor, getPos }) => {
      const wrapper = document.createElement('span');
      wrapper.classList.add('variable-select');
      wrapper.dataset.variable = node.attrs.variable;
      wrapper.style.position = 'relative';

      const display = document.createElement('div');
      display.classList.add('variable-select-control');
      wrapper.appendChild(display);

      const chevron = document.createElement('span');
      chevron.classList.add('chevron');
      chevron.textContent = '▾';
      chevron.style.marginLeft = '12px';
      display.appendChild(chevron);

      const dropdown = document.createElement('div');
      dropdown.classList.add('variable-select-dropdown');
      wrapper.appendChild(dropdown);

      // Parse value string into array — single-select values must not be split since they may contain commas
      const getSelectedValues = () => {
        if (!node.attrs.value) return [];
        if (!node.attrs.multiple) return [node.attrs.value];
        return node.attrs.value
          .split(',')
          .map((v) => v.trim())
          .filter(Boolean);
      };

      // Build a value→label lookup from options
      const buildValueToLabel = (options) =>
        Object.fromEntries(
          (options || []).map((opt) => {
            if (typeof opt === 'string') return [opt, opt.charAt(0).toUpperCase() + opt.slice(1)];
            return [opt.value || '', opt.label || opt.value || ''];
          }),
        );

      // Update display: show labels for selected values
      const updateDisplay = (overrideSelected) => {
        const selected = overrideSelected ?? getSelectedValues();
        const vToL = buildValueToLabel(node.attrs.options);
        let displayText = '';

        if (selected.length === 0) {
          displayText = node.attrs.placeholder || 'Select an option';
          display.style.color = '#6a6765';
        } else if (selected.length <= 2) {
          displayText = selected.map((v) => vToL[v] || v.charAt(0).toUpperCase() + v.slice(1)).join(', ');
          display.style.color = '#000';
        } else {
          const firstTwo = selected.slice(0, 2);
          displayText = firstTwo.map((v) => vToL[v] || v.charAt(0).toUpperCase() + v.slice(1)).join(', ') + '...';
          display.style.color = '#000';
        }

        display.textContent = displayText;
        display.appendChild(chevron);
      };
      updateDisplay();

      // Populate dropdown options
      (node.attrs.options || []).forEach((opt) => {
        const optValue = typeof opt === 'string' ? opt : opt.value || '';
        const optLabel =
          typeof opt === 'string' ? opt.charAt(0).toUpperCase() + opt.slice(1) : opt.label || opt.value || '';

        const item = document.createElement('div');
        item.classList.add('variable-select-dropdown-option');
        item.dataset.value = optValue;
        item.style.display = 'flex';
        item.style.alignItems = 'center';
        item.style.gap = '6px';

        const check = document.createElement('span');
        check.style.width = '16px';
        check.style.height = '16px';
        check.style.flexShrink = '0';

        const text = document.createElement('span');
        text.textContent = optLabel;

        const updateItem = () => {
          const selected = getSelectedValues();
          const isSelected = selected.includes(optValue);

          if (isSelected) {
            item.appendChild(check);
            item.appendChild(text);
            check.innerHTML = `
            <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6222e3" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="20 6 9 17 4 12"></polyline>
            </svg>
          `;
            text.style.color = '#6222e3';
          } else {
            item.appendChild(text);
            check.innerHTML = '';
            text.style.color = '#000';
          }
        };
        updateItem();

        item.addEventListener('mouseenter', () => (item.style.background = '#f0f0f0'));
        item.addEventListener('mouseleave', () => (item.style.background = '#fff'));

        item.addEventListener('click', (e) => {
          e.stopPropagation();
          let selected = getSelectedValues();

          if (node.attrs.multiple) {
            if (selected.includes(optValue)) selected = selected.filter((v) => v !== optValue);
            else selected = [...selected, optValue];
          } else {
            selected = [optValue];
          }

          const newValue = node.attrs.multiple ? selected.join(',') : selected[0] || '';
          const vToL = buildValueToLabel(node.attrs.options);
          const newLabel = selected.map((v) => vToL[v] || v).join(', ');
          const { tr } = editor.state;
          tr.setNodeMarkup(getPos(), undefined, { ...node.attrs, value: newValue, label: newLabel });
          editor.view.dispatch(tr);

          updateDisplay(selected);

          // Update all items' checkmarks
          Array.from(dropdown.children).forEach((child) => {
            const childValue = child.dataset?.value;
            if (childValue === undefined) return;
            const spanTextElem = child.querySelector('span:nth-child(2)');
            const spanCheck = child.querySelector('span:nth-child(1)');
            if (!spanTextElem || !spanCheck) return;

            const isSel = selected.includes(childValue);
            if (isSel) {
              spanCheck.innerHTML = `
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#6222e3" stroke-width="3" stroke-linecap="round" stroke-linejoin="round">
                <polyline points="20 6 9 17 4 12"></polyline>
              </svg>
            `;
              spanTextElem.style.color = '#6222e3';
            } else {
              spanCheck.innerHTML = '';
              spanTextElem.style.color = '#000';
            }
          });
        });

        dropdown.appendChild(item);
      });

      // Toggle dropdown
      display.addEventListener('click', (e) => {
        e.stopPropagation();

        // Close other dropdowns
        document.querySelectorAll('.variable-select-dropdown.open').forEach((dd) => {
          if (dd !== dropdown) {
            dd.style.display = 'none';
            dd.classList.remove('open');
            dd.parentElement?.classList.remove('selected');
          }
        });

        const open = dropdown.classList.toggle('open');

        if (open) {
          requestAnimationFrame(() => {
            updateArrows();
            dropdown.addEventListener('scroll', updateArrows);
          });

          // --- Container boundaries ---
          const container = wrapper.closest('.assist-input');
          if (!container) return;

          const displayRect = display.getBoundingClientRect();

          const spaceAbove = displayRect.top - 42;

          // Apply max-height using calc for safety
          dropdown.style.maxHeight = `calc(${Math.max(120, spaceAbove)}px)`; // // `${Math.max(0, maxHeight - 4)}px`; // leave small padding

          dropdown.style.top = 'auto';
          dropdown.style.bottom = `${display.offsetHeight + 2}px`;

          dropdown.style.display = 'block';
          dropdown.scrollTop = 0;
          wrapper.classList.add('selected');
        } else {
          dropdown.style.display = 'none';
          wrapper.classList.remove('selected');
        }
      });

      let doc = null;
      try {
        // Only access if editor.view exists
        doc = editor?.view?.dom?.ownerDocument || window.document;
      } catch {
        doc = window.document;
      }

      const handleOutsideClick = (e) => {
        if (!wrapper.contains(e.target)) {
          dropdown.style.display = 'none';
          dropdown.classList.remove('open');
          wrapper.classList.remove('selected');
        }
      };

      doc.addEventListener('click', handleOutsideClick, true);

      const updateArrows = () => {
        const scrollTop = dropdown.scrollTop;
        const scrollHeight = dropdown.scrollHeight;
        const clientHeight = dropdown.clientHeight;

        if (scrollTop === 0) {
          dropdown.classList.toggle('scroll-bottom', scrollTop + clientHeight < scrollHeight);
        } else {
          dropdown.classList.remove('scroll-bottom');
        }
      };

      return {
        dom: wrapper,
        contentDOM: null,
        stopEvent: (event) => wrapper.contains(event.target),
        destroy: () => doc.removeEventListener('click', handleOutsideClick, true),
      };
    };
  },
});
